import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useTournamentPlayers } from "@/graphql"
import { ArrowDownAZ, ArrowUpAZ, Clock } from "lucide-react"
import { useState } from "react"
import { useParams } from "react-router-dom"

type SortMode = "name" | "created"
type SortDir = "asc" | "desc"

export const ListPlayers = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>()
  const { players, loading, error } = useTournamentPlayers(Number(tournamentId), true)
  const [sortMode, setSortMode] = useState<SortMode>("name")
  const [sortDir, setSortDir] = useState<SortDir>("asc")

  const handleSort = (mode: SortMode) => {
    if (mode === sortMode) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortMode(mode)
      setSortDir("asc")
    }
  }

  const sortedPlayers = [...(players || [])].sort((a, b) => {
    const cmp =
      sortMode === "created"
        ? new Date(a.created).getTime() - new Date(b.created).getTime()
        : a.Person.fullName.localeCompare(b.Person.fullName)
    return sortDir === "asc" ? cmp : -cmp
  })

  const dropped = [...sortedPlayers].filter((x) => x.drop)

  if (loading || error) {
    return null
  }

  return (
    players &&
    players.length > 0 && (
      <Card className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold leading-tight">
                Jugadores Inscritos
                <span className="ml-1.5 text-sm font-normal text-muted-foreground">
                  ({sortedPlayers.length})
                </span>
              </CardTitle>
              {dropped.length > 0 && (
                <CardDescription className="mt-0.5 text-xs text-muted-foreground dark:text-gray-400">
                  Activos: {sortedPlayers.length - dropped.length} · Drop: {dropped.length}
                </CardDescription>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button
                variant={sortMode === "name" ? "default" : "ghost"}
                size="icon"
                className="h-7 w-7"
                title="Ordenar por nombre"
                onClick={() => handleSort("name")}
              >
                {sortMode === "name" && sortDir === "desc" ? (
                  <ArrowUpAZ className="h-3.5 w-3.5" />
                ) : (
                  <ArrowDownAZ className="h-3.5 w-3.5" />
                )}
              </Button>
              <Button
                variant={sortMode === "created" ? "default" : "ghost"}
                size="icon"
                className="h-7 w-7"
                title="Ordenar por hora de inscripción"
                onClick={() => handleSort("created")}
              >
                <Clock className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {sortedPlayers.map((player, key) => (
              <div
                key={key}
                className="flex justify-between items-center text-sm border-b border-muted/50 dark:border-gray-700 py-1"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="truncate">{player.Person?.fullName}</span>
                  {player.drop === 1 && (
                    <Badge
                      variant="destructive"
                      className="text-[10px] px-1 py-0 leading-tight shrink-0"
                    >
                      Drop
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {player.created && (
                    <span className="text-xs text-muted-foreground dark:text-gray-400">
                      {new Date(player.created).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  )
}
